import React, { useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface CounterOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  counterOfferData: {
    originalOffer: {
      position: string;
      salary: string;
      salaryPeriod: string;
      currencyType: string;
      contractType: string;
      startDate: string;
    };
    counterOffer: {
      requestedSalary: string;
      preferredStartDate?: string;
      notes?: string;
    };
  };
  onApprove: () => void;
  onDecline: () => void;
  onSendRevised: () => void;
  isReadOnly?: boolean; // Read-only prop for approved/rejected counter offers
  isRejected?: boolean; // Indicate if counter offer was rejected
}

export default function CounterOfferModal({
  isOpen,
  onClose,
  counterOfferData,
  onApprove,
  onDecline,
  onSendRevised,
  isReadOnly = false,
  isRejected = false,
}: CounterOfferModalProps) {
  const { t } = useTranslation("job-details");

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatCurrency = (amount: string, currency: string) => {
    return `${currency} ${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="counter-offer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-inner">
            <div className="modal-icon">
              {isReadOnly && !isRejected ? (
                <i className="fa fa-check-circle" style={{ fontSize: "36px", color: "#10b981" }}></i>
              ) : isRejected ? (
                <i className="fa fa-times-circle" style={{ fontSize: "36px", color: "#ef4444" }}></i>
              ) : (
                <i className="fa fa-handshake-o" style={{ fontSize: "36px", color: "#4f46e5" }}></i>
              )}
            </div>
            <h3>
              {isReadOnly && !isRejected
                ? "Approved Counter Offer"
                : isRejected
                  ? "Rejected Counter Offer"
                  : "Review Counter Offer"}
            </h3>
          </div>
          <p>
            {isReadOnly && !isRejected
              ? "You have approved the candidate's counter offer. Below are the agreed terms."
              : isRejected
                ? "You have rejected the candidate's counter offer. Below are the details of the rejected proposal."
                : "The candidate has submitted a counter offer. Review the details below and respond accordingly."}
          </p>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          {/* Original Offer Section */}
          <div className="offer-section original-offer">
            <h4>Original Offer</h4>
            <div className="offer-details-grid">
              <div className="detail-item">
                <label>Position:</label>
                <span>{counterOfferData.originalOffer.position}</span>
              </div>
              <div className="detail-item">
                <label>Salary:</label>
                <span>
                  {formatCurrency(counterOfferData.originalOffer.salary, counterOfferData.originalOffer.currencyType)} /{" "}
                  {counterOfferData.originalOffer.salaryPeriod}
                </span>
              </div>
              <div className="detail-item">
                <label>Contract Type:</label>
                <span>{counterOfferData.originalOffer.contractType}</span>
              </div>
              <div className="detail-item">
                <label>Start Date:</label>
                <span>{formatDate(counterOfferData.originalOffer.startDate)}</span>
              </div>
            </div>
          </div>

          {/* Counter Offer Section */}
          <div className="offer-section counter-offer">
            <h4>Counter Offer</h4>
            <div className="counter-offer-highlight">
              <div className="detail-item">
                <label>Requested Salary:</label>
                <span className="highlight-value">
                  {formatCurrency(
                    counterOfferData.counterOffer.requestedSalary,
                    counterOfferData.originalOffer.currencyType,
                  )}{" "}
                  / {counterOfferData.originalOffer.salaryPeriod}
                </span>
              </div>
              {counterOfferData.counterOffer.preferredStartDate && (
                <div className="detail-item">
                  <label>Preferred Start Date:</label>
                  <span className="highlight-value">
                    {formatDate(counterOfferData.counterOffer.preferredStartDate)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Notes */}
          {counterOfferData.counterOffer.notes && (
            <div className="offer-section notes-section">
              <h4>Additional Notes</h4>
              <div className="notes-content">
                <p>{counterOfferData.counterOffer.notes}</p>
              </div>
            </div>
          )}

          {/* Tip Section - Only show if not read-only */}
          {!isReadOnly && (
            <div className="tip-section">
              <i className="fa fa-lightbulb-o"></i>
              <span>Tip: Decline with a message to keep the conversation open.</span>
            </div>
          )}

          {/* Approval/Rejection Badge - Show only in read-only mode */}
          {isReadOnly && !isRejected && (
            <div
              className="offer-section"
              style={{
                background: "#d1fae5",
                border: "1px solid #10b981",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "#065f46",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                <i className="fa fa-check-circle" style={{ fontSize: "20px" }}></i>
                <span>Counter Offer Approved</span>
              </div>
            </div>
          )}

          {/* Rejection Badge - Show only when rejected */}
          {isRejected && (
            <div
              className="offer-section"
              style={{
                background: "#fee2e2",
                border: "1px solid #ef4444",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "#991b1b",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                <i className="fa fa-times-circle" style={{ fontSize: "20px" }}></i>
                <span>Counter Offer Rejected</span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons - Only show if not read-only */}
        {!isReadOnly && (
          <div className="d-flex align-items-center justify-content-center p-4" style={{ gap: "12px" }}>
            <button
              className="btn-get-hire"
              onClick={onApprove}
              style={{ flex: "1", minWidth: "150px", height: "48px", fontSize: "18px" }}
            >
              Approve New Offer
            </button>
            <button
              className="btn-reject"
              onClick={onDecline}
              style={{ flex: "1", minWidth: "150px", height: "48px", fontSize: "18px" }}
            >
              Decline Counter
            </button>
            <button
              className="find-btn"
              onClick={onSendRevised}
              style={{ flex: "1", minWidth: "150px", height: "48px", fontSize: "18px", background: "#1a1a1a" }}
            >
              Send Revised Offer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
